/**
 * NeoCoreConciliacionPage — "Conciliación".
 *
 * Vista única para verificar la conciliación contra el banco: junta ENTRADAS
 * (reportes de pago: ingresos, ahorro, crédito) y SALIDAS (retiros acreditados
 * por transferencia), por cuenta (BAC / Interbanco) y rango de fechas.
 *
 * Conciliado = tiene REFERENCIA del estado de cuenta (referencia_banco). Lo que
 * falta referencia se resalta: son las que hay que cruzar contra el banco.
 * En entradas la referencia se edita inline (set_referencia_banco_admin); en
 * salidas se pone al acreditar el retiro.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { ArrowsClockwise, Copy, Check, DownloadSimple, MagnifyingGlass, Warning, PencilSimple, ArrowDown, ArrowUp, Image as ImageIcon, X } from '@phosphor-icons/react'
import { supabase } from '@/lib/supabase'

type Direccion = 'entrada' | 'salida'
type Row = {
  id: string
  reporteId: string | null      // id del reporte_pago (para editar referencia); null en salidas
  direccion: Direccion
  concepto: string | null
  cuenta: string | null         // BAC Monetaria / Interbanco Ahorro / —
  forma_pago: string | null
  autorizacion: string | null   // del asociado (solo entradas)
  referencia: string | null     // del estado de cuenta (concilia contra el banco)
  monto: number
  estado: string | null
  created_at: string
  asociado_neo: string
  asociado_nombre: string
  comprobantePath: string | null
  comprobanteBucket: 'reportes' | 'retiros' | 'kyc'
}

const CONCEPTO: Record<string, string> = {
  INGRESO: 'Ingreso', AHORRO_DEPOSITO: 'Ahorro', CREDITO_CUOTA: 'Cuota crédito',
  APORTACIONES_EXTRA: 'Aportación', PLAZO_FIJO_APERTURA: 'Plazo fijo', OTRO: 'Otro', RETIRO: 'Retiro',
}
const fmtQ = (n: number) => 'Q ' + Number(n ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const hoyGT = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/Guatemala' })
const fmtHora = (iso: string) => new Date(iso).toLocaleString('es-GT', {
  timeZone: 'America/Guatemala', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false,
})

export function NeoCoreConciliacionPage() {
  const [desde, setDesde] = useState(hoyGT())
  const [hasta, setHasta] = useState(hoyGT())
  const [rows, setRows] = useState<Row[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [copied, setCopied] = useState<string | null>(null)
  const [soloPend, setSoloPend] = useState(false)
  const [dir, setDir] = useState<'todos' | Direccion>('todos')
  const [cuentaFiltro, setCuentaFiltro] = useState<string>('todas')
  const [editRow, setEditRow] = useState<{ id: string; autorizacion: string; referencia: string } | null>(null)
  const [savingRef, setSavingRef] = useState(false)
  const [verImg, setVerImg] = useState<{ url: string; nombre: string } | null>(null)
  const [imgLoading, setImgLoading] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    const desdeIso = `${desde}T00:00:00-06:00`
    const hastaIso = `${hasta}T23:59:59.999-06:00`

    const [entradas, salidas, cuentas] = await Promise.all([
      supabase.from('reporte_pago_asociado')
        .select('id, concepto, forma_pago, numero_referencia, referencia_banco, monto, estado, created_at, fecha_movimiento, comprobante_storage_path, cuenta:cuenta_bancaria_propia_id (alias), asociado:asociado_id (neo_id, nombre_completo, comprobante_aporte_url)')
        // Rechazados = plata que NO entró: fuera de la conciliación (evita inflar Entradas).
        .neq('estado', 'rechazado')
        .gte('created_at', desdeIso).lte('created_at', hastaIso)
        .order('created_at', { ascending: false }).limit(1000),
      supabase.from('retiro')
        .select('id, monto, estado, forma_pago, cuenta_salida_codigo, referencia_banco, procesado_at, comprobante_titularidad_path, asociado:asociado_id (neo_id, nombre_completo)')
        .eq('estado', 'acreditado').eq('forma_pago', 'TRANSFERENCIA')
        .gte('procesado_at', desdeIso).lte('procesado_at', hastaIso)
        .order('procesado_at', { ascending: false }).limit(1000),
      supabase.from('cuenta_bancaria_propia').select('cuenta_contable_codigo, alias'),
    ])

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const codeToAlias = new Map<string, string>((cuentas.data ?? []).map((c: any) => [c.cuenta_contable_codigo, c.alias]))

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const rowsE: Row[] = (entradas.data ?? []).map((r: any) => {
      const esIngreso = r.concepto === 'INGRESO'
      return {
        id: `e-${r.id}`, reporteId: r.id, direccion: 'entrada' as Direccion, concepto: r.concepto,
        cuenta: r.cuenta?.alias ?? null, forma_pago: r.forma_pago,
        autorizacion: r.numero_referencia, referencia: r.referencia_banco,
        monto: Number(r.monto ?? 0), estado: r.estado,
        created_at: esIngreso && r.fecha_movimiento ? `${r.fecha_movimiento}T12:00:00-06:00` : r.created_at,
        asociado_neo: r.asociado?.neo_id ?? '—', asociado_nombre: r.asociado?.nombre_completo ?? '—',
        // Ingreso (inscripción): la boleta vive en el bucket kyc (comprobante_aporte_url).
        // Ahorro/crédito: en el bucket reportes (comprobante_storage_path).
        comprobantePath: esIngreso ? (r.asociado?.comprobante_aporte_url ?? null) : (r.comprobante_storage_path ?? null),
        comprobanteBucket: (esIngreso ? 'kyc' : 'reportes') as Row['comprobanteBucket'],
      }
    })

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const rowsS: Row[] = (salidas.data ?? []).map((r: any) => ({
      id: `s-${r.id}`, reporteId: null, direccion: 'salida' as Direccion, concepto: 'RETIRO',
      cuenta: codeToAlias.get(r.cuenta_salida_codigo) ?? null, forma_pago: r.forma_pago,
      autorizacion: null, referencia: r.referencia_banco,
      monto: Number(r.monto ?? 0), estado: 'acreditado',
      created_at: r.procesado_at, asociado_neo: r.asociado?.neo_id ?? '—', asociado_nombre: r.asociado?.nombre_completo ?? '—',
      comprobantePath: r.comprobante_titularidad_path ?? null, comprobanteBucket: 'retiros',
    }))

    setRows([...rowsE, ...rowsS].sort((a, b) => (a.created_at < b.created_at ? 1 : -1)))
    setLoading(false)
  }, [desde, hasta])

  useEffect(() => { void load() }, [load])

  const cuentas = useMemo(() => Array.from(new Set(rows.map((r) => r.cuenta).filter(Boolean))) as string[], [rows])

  const filtered = useMemo(() => {
    let list = rows
    if (dir !== 'todos') list = list.filter((r) => r.direccion === dir)
    if (cuentaFiltro !== 'todas') list = list.filter((r) => r.cuenta === cuentaFiltro)
    if (soloPend) list = list.filter((r) => !r.referencia?.trim())
    const q = search.trim().toLowerCase()
    if (q) list = list.filter((r) =>
      r.asociado_nombre.toLowerCase().includes(q) || r.asociado_neo.includes(q) ||
      (r.referencia ?? '').includes(q) || (r.autorizacion ?? '').includes(q) || (r.cuenta ?? '').toLowerCase().includes(q))
    return list
  }, [rows, search, soloPend, dir, cuentaFiltro])

  const stats = useMemo(() => {
    const ent = filtered.filter((r) => r.direccion === 'entrada')
    const sal = filtered.filter((r) => r.direccion === 'salida')
    const totalEnt = ent.reduce((s, r) => s + r.monto, 0)
    const totalSal = sal.reduce((s, r) => s + r.monto, 0)
    return {
      totalEnt, totalSal, neto: totalEnt - totalSal,
      sinRef: filtered.filter((r) => !r.referencia?.trim()).length,
      n: filtered.length,
    }
  }, [filtered])

  const verComprobante = async (r: Row) => {
    if (!r.comprobantePath) return
    setImgLoading(r.id)
    const { data } = await supabase.storage.from(r.comprobanteBucket).createSignedUrl(r.comprobantePath, 60 * 60)
    setImgLoading(null)
    if (data?.signedUrl) setVerImg({ url: data.signedUrl, nombre: r.asociado_nombre })
  }

  const copy = (text: string, key: string) => {
    navigator.clipboard?.writeText(text).then(() => {
      setCopied(key); setTimeout(() => setCopied((c) => (c === key ? null : c)), 1400)
    })
  }
  const saveRow = async () => {
    if (!editRow) return
    const r = rows.find((x) => x.id === editRow.id)
    if (!r?.reporteId) { setEditRow(null); return }
    setSavingRef(true)
    const { data, error } = await supabase.rpc('set_referencias_reporte_admin', {
      p_reporte_id: r.reporteId, p_numero_referencia: editRow.autorizacion, p_referencia_banco: editRow.referencia,
    })
    setSavingRef(false)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const res = data as any
    if (!error && res?.ok) {
      setRows((prev) => prev.map((x) => (x.id === editRow.id
        ? { ...x, autorizacion: res.numero_referencia ?? null, referencia: res.referencia_banco ?? null } : x)))
      setEditRow(null)
    } else if (error) {
      alert(error.message)
    }
  }

  const exportCSV = () => {
    const header = ['fecha', 'direccion', 'cuenta', 'concepto', 'asociado', 'neo_id', 'autorizacion', 'referencia', 'monto', 'estado']
    const esc = (v: unknown) => { const s = String(v ?? '').replace(/"/g, '""'); return /[",\n]/.test(s) ? `"${s}"` : s }
    const lines = [header.join(',')]
    for (const r of filtered) lines.push([
      fmtHora(r.created_at), r.direccion, r.cuenta ?? '', CONCEPTO[r.concepto ?? ''] ?? r.concepto ?? '',
      r.asociado_nombre, r.asociado_neo, r.autorizacion ?? '', r.referencia ?? '',
      (r.direccion === 'salida' ? -r.monto : r.monto).toFixed(2), r.estado ?? '',
    ].map(esc).join(','))
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' })
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob)
    a.download = `conciliacion_${desde}_a_${hasta}.csv`; a.click()
  }

  const rango = (d: number) => {
    const h = hoyGT()
    const from = new Date(); from.setDate(from.getDate() - d)
    setDesde(from.toLocaleDateString('en-CA', { timeZone: 'America/Guatemala' })); setHasta(h)
  }

  return (
    <div style={{ padding: '20px 24px', maxWidth: 1300, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 16, gap: 16, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800 }}>Conciliación</h1>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--muted-foreground)' }}>
            Entradas y salidas por cuenta. Conciliado = tiene referencia del estado de cuenta. Tocá una referencia y se copia.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={exportCSV} disabled={filtered.length === 0} className="btn btn-ghost"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <DownloadSimple size={14} weight="bold" /> CSV
          </button>
          <button onClick={() => void load()} className="btn btn-ghost" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <ArrowsClockwise size={13} weight="bold" /> Refrescar
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
        <Kpi label="Entradas" value={fmtQ(stats.totalEnt)} tone="ok" />
        <Kpi label="Salidas" value={fmtQ(stats.totalSal)} tone="warn" />
        <Kpi label="Neto" value={fmtQ(stats.neto)} />
        <Kpi label="Sin conciliar" value={String(stats.sinRef)} tone={stats.sinRef > 0 ? 'warn' : 'ok'} sub="falta referencia" />
      </div>

      {/* Filtros */}
      <div style={{ background: 'var(--surface)', border: '1px solid var(--hairline)', borderRadius: 12, padding: '12px 14px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 4 }}>
          {([['Hoy', 0], ['7 días', 7], ['30 días', 30]] as Array<[string, number]>).map(([l, d]) => (
            <button key={l} onClick={() => rango(d)} style={{ all: 'unset', cursor: 'pointer', padding: '6px 12px', borderRadius: 99, fontSize: 12, fontWeight: 700, color: 'var(--ink-soft)', border: '1px solid var(--hairline)' }}>{l}</button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 4 }}>
          {([['todos', 'Todo'], ['entrada', 'Entradas'], ['salida', 'Salidas']] as Array<['todos' | Direccion, string]>).map(([v, l]) => (
            <button key={v} onClick={() => setDir(v)} style={{ all: 'unset', cursor: 'pointer', padding: '6px 12px', borderRadius: 99, fontSize: 12, fontWeight: 700,
              color: dir === v ? 'white' : 'var(--ink-soft)', background: dir === v ? 'var(--brand)' : 'transparent',
              border: `1px solid ${dir === v ? 'var(--brand)' : 'var(--hairline)'}` }}>{l}</button>
          ))}
        </div>
        <select value={cuentaFiltro} onChange={(e) => setCuentaFiltro(e.target.value)}
          style={{ padding: '6px 10px', border: '1px solid var(--hairline)', borderRadius: 8, fontSize: 12, fontWeight: 700, color: 'var(--ink-soft)' }}>
          <option value="todas">Todas las cuentas</option>
          {cuentas.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} style={{ padding: '6px 10px', border: '1px solid var(--hairline)', borderRadius: 8, fontSize: 13 }} />
        <span style={{ color: 'var(--muted-foreground)' }}>→</span>
        <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} style={{ padding: '6px 10px', border: '1px solid var(--hairline)', borderRadius: 8, fontSize: 13 }} />
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, color: 'var(--ink-soft)', cursor: 'pointer' }}>
          <input type="checkbox" checked={soloPend} onChange={(e) => setSoloPend(e.target.checked)} /> Solo sin conciliar
        </label>
        <div style={{ flex: 1 }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'var(--bg)', borderRadius: 8, padding: '6px 10px', border: '1px solid var(--hairline)', minWidth: 220 }}>
          <MagnifyingGlass size={14} weight="bold" color="var(--muted-foreground)" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Referencia, asociado, cuenta…"
            style={{ flex: 1, border: 0, background: 'transparent', outline: 'none', fontSize: 13, color: 'var(--ink)' }} />
        </div>
      </div>

      {/* Tabla */}
      <div style={{ background: 'var(--surface)', border: '1px solid var(--hairline)', borderRadius: 12, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted-foreground)' }}>Cargando…</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted-foreground)' }}>Sin movimientos en este rango.</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--hairline)', background: 'var(--bg)' }}>
                <th style={th}>Fecha</th><th style={th}>Asociado</th><th style={th}>Tipo</th><th style={th}>Cuenta</th>
                <th style={th}>Autorización / referencia</th>
                <th style={{ ...th, textAlign: 'right' }}>Monto</th>
                <th style={{ ...th, textAlign: 'center' }}>Boleta</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const auth = r.autorizacion?.trim()
                const salida = r.direccion === 'salida'
                return (
                  <tr key={r.id} style={{ borderBottom: '1px solid var(--hairline)' }}>
                    <td style={{ ...td, whiteSpace: 'nowrap', color: 'var(--muted-foreground)' }}>{fmtHora(r.created_at)}</td>
                    <td style={td}>
                      <div style={{ fontWeight: 600 }}>{r.asociado_nombre}</div>
                      <div style={{ fontSize: 11, color: 'var(--muted-foreground)' }}>{r.asociado_neo}</div>
                    </td>
                    <td style={td}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 700,
                        color: salida ? 'var(--danger, #a32d2d)' : 'var(--ok-ink, #0f8a57)' }}>
                        {salida ? <ArrowUp size={12} weight="bold" /> : <ArrowDown size={12} weight="bold" />}
                        {CONCEPTO[r.concepto ?? ''] ?? r.concepto}
                      </span>
                    </td>
                    <td style={{ ...td, fontSize: 12 }}>{r.cuenta ?? '—'}</td>
                    <td style={td}>
                      {editRow?.id === r.id ? (
                        <div style={{ display: 'grid', gap: 4 }}>
                          <input autoFocus value={editRow.autorizacion}
                            onChange={(e) => setEditRow({ ...editRow, autorizacion: e.target.value })}
                            onKeyDown={(e) => { if (e.key === 'Enter') void saveRow(); if (e.key === 'Escape') setEditRow(null) }}
                            placeholder="autorización (del asociado)"
                            style={{ width: 200, fontSize: 11, fontFamily: 'var(--font-mono, monospace)', padding: '4px 6px', border: '1px solid var(--brand)', borderRadius: 6, outline: 'none' }} />
                          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            <input value={editRow.referencia}
                              onChange={(e) => setEditRow({ ...editRow, referencia: e.target.value })}
                              onKeyDown={(e) => { if (e.key === 'Enter') void saveRow(); if (e.key === 'Escape') setEditRow(null) }}
                              placeholder="referencia (estado de cuenta)"
                              style={{ width: 200, fontSize: 11, fontFamily: 'var(--font-mono, monospace)', padding: '4px 6px', border: '1px solid var(--brand)', borderRadius: 6, outline: 'none' }} />
                            <button onClick={() => void saveRow()} disabled={savingRef} title="Guardar" style={{ all: 'unset', cursor: 'pointer', color: 'var(--ok-ink, #0f8a57)' }}><Check size={14} weight="bold" /></button>
                            <button onClick={() => setEditRow(null)} title="Cancelar" style={{ all: 'unset', cursor: 'pointer', color: 'var(--muted-foreground)', fontSize: 12, fontWeight: 700 }}>✕</button>
                          </div>
                        </div>
                      ) : (
                        <div style={{ display: 'inline-flex', alignItems: 'flex-start', gap: 8 }}>
                          <div>
                            {/* Autorización (entradas) */}
                            {!salida && (auth ? (
                              <button onClick={() => copy(auth, `a-${r.id}`)} title="Copiar autorización"
                                style={{ all: 'unset', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6,
                                  fontFamily: 'var(--font-mono, monospace)', fontSize: 12, fontWeight: 700,
                                  color: copied === `a-${r.id}` ? 'var(--ok-ink, #0f8a57)' : 'var(--ink)' }}>
                                {auth}{copied === `a-${r.id}` ? <Check size={11} weight="bold" /> : <Copy size={11} />}
                              </button>
                            ) : (
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 700, color: 'var(--warning, #d97706)' }}>
                                <Warning size={11} weight="bold" /> sin autorización
                              </span>
                            ))}
                            {/* Referencia del estado de cuenta */}
                            <div style={{ marginTop: salida ? 0 : 4 }}>
                              {r.referencia?.trim() ? (
                                <button onClick={() => copy(r.referencia!.trim(), `r-${r.id}`)} title="Copiar referencia"
                                  style={{ all: 'unset', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: 'var(--font-mono, monospace)', fontSize: 12, fontWeight: 700, color: 'var(--brand)' }}>
                                  ref: {r.referencia.trim()}{copied === `r-${r.id}` ? <Check size={11} weight="bold" /> : <Copy size={11} />}
                                </button>
                              ) : (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 700, color: 'var(--warning, #d97706)' }}>
                                  <Warning size={11} weight="bold" /> sin conciliar
                                </span>
                              )}
                            </div>
                          </div>
                          {r.reporteId && (
                            <button onClick={() => setEditRow({ id: r.id, autorizacion: r.autorizacion ?? '', referencia: r.referencia ?? '' })}
                              title="Conciliar manual (editar autorización + referencia)"
                              style={{ all: 'unset', cursor: 'pointer', color: 'var(--muted-foreground)', marginTop: 2 }}>
                              <PencilSimple size={13} />
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 700, fontFeatureSettings: '"tnum"', whiteSpace: 'nowrap',
                      color: salida ? 'var(--danger, #a32d2d)' : 'var(--ink)' }}>
                      {salida ? '− ' : ''}{fmtQ(r.monto)}
                    </td>
                    <td style={{ ...td, textAlign: 'center' }}>
                      {r.comprobantePath ? (
                        <button onClick={() => void verComprobante(r)} disabled={imgLoading === r.id} title="Ver boleta"
                          style={{ all: 'unset', cursor: 'pointer', color: 'var(--brand)', display: 'inline-flex' }}>
                          {imgLoading === r.id ? <ArrowsClockwise size={16} weight="bold" /> : <ImageIcon size={18} weight="duotone" />}
                        </button>
                      ) : <span style={{ color: 'var(--muted-foreground)', fontSize: 11 }}>—</span>}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>

      {verImg && (
        <div onClick={() => setVerImg(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.78)', zIndex: 1000,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', maxWidth: 720, color: '#fff', marginBottom: 8 }}>
            <span style={{ fontWeight: 700 }}>{verImg.nombre}</span>
            <button onClick={() => setVerImg(null)} title="Cerrar" style={{ all: 'unset', cursor: 'pointer', color: '#fff' }}><X size={22} weight="bold" /></button>
          </div>
          <img src={verImg.url} alt="Boleta" onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: '100%', maxHeight: '80vh', borderRadius: 12, objectFit: 'contain', background: '#fff' }} />
        </div>
      )}
    </div>
  )
}

function Kpi({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: 'ok' | 'warn' }) {
  const color = tone === 'ok' ? 'var(--ok-ink, #0f8a57)' : tone === 'warn' ? 'var(--warning, #d97706)' : 'var(--ink)'
  return (
    <div style={{ flex: 1, minWidth: 160, background: 'var(--surface)', border: '1px solid var(--hairline)', borderRadius: 12, padding: '14px 16px' }}>
      <p style={{ margin: 0, fontSize: 10, fontWeight: 800, letterSpacing: 1.2, textTransform: 'uppercase', color: 'var(--muted-foreground)' }}>{label}</p>
      <p style={{ margin: '6px 0 0', fontSize: 20, fontWeight: 800, color, fontFeatureSettings: '"tnum"' }}>
        {value}{sub && <span style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--muted-foreground)' }}>{sub}</span>}
      </p>
    </div>
  )
}

const th: React.CSSProperties = { textAlign: 'left', padding: '10px 14px', fontSize: 10, fontWeight: 800, color: 'var(--muted-foreground)', textTransform: 'uppercase', letterSpacing: 1.2 }
const td: React.CSSProperties = { padding: '12px 14px' }
